import {
  Box,
  Divider,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
  FormControl,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  ForwardedRef,
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from 'react';
import {
  Control,
  useFieldArray,
  useForm,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormWatch,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import DeleteFileRedIcon from '../../../../Icons/DeleteFileRedIcon';
import CustomButton from '../../../../MUIComponents/CustomButton';
import Input from '../../../../MUIComponents/Input';
import DateInput from '../../../../MUIComponents/DateInput';
import { ButtonTypeEnum } from '../../../../../enums/button-type-enum';
import { OTHER_SPORT } from '../../../../../constants';
import { useGetSportsQuery } from '../../../../../store/apiSlice/sportApi';
import SportList from '../../SportVerification/SportList/SportList';
import DragAndDropBox from '../../../../MUIComponents/DragAndDropBox/DragAndDropBox';
import UploadIcon from '../../../../Icons/UploadIcon';
import SportBackgroundImageFileItem from '../../SportVerification/SportBackgroundImageFileItem/SportBackgroundImageFileItem';
import { ElementIdEnum } from '../../../../../enums/element-id-enum';
import {
  isSupportedSportBackgroundFileHelper,
  validateFileSize,
} from '../../../../../utils/helper/file-validation-helper';
import {
  useGetLeagueBySportNameQuery,
  useGetTeamsByLeagueIdOrExternalIdQuery,
} from '../../../../../store/apiSlice/careerApi';
import { withUnderScore } from '../../../../../utils/withUnderScore';
import ArrowDownIcon from '../../../../Icons/ArrowDownIcon';
import AutocompleteOption from '../../../../shared/AutocompleteOption/AutocompleteOption';
import {
  ISportBackground,
  ITeam,
  ISportBackgroundFileData,
} from '../../../../../interfaces/user-sport-background.interface';
import { createSportBackgroundSchema } from '../../../../../validation/sport-validation';

export interface OnboardingSportBackgroundFormInputs {
  sportBackgrounds: ISportBackground[];
}

const onboardingSportBackgroundFormSchema: yup.AnyObjectSchema = yup.object({
  sportBackgrounds: yup
    .array()
    .of(
      yup.lazy((sb: any) => {
        const isOtherSport = !!sb?.isOtherSport;

        return createSportBackgroundSchema(isOtherSport).concat(
          yup.object({
            sportId: yup.string().required('Sport is required'),
          })
        );
      })
    )
    .min(1, 'At least one sport is required')
    .required('Sport backgrounds are required'),
});

export interface FormRef {
  control: Control<OnboardingSportBackgroundFormInputs, any>;
  reset: UseFormReset<OnboardingSportBackgroundFormInputs>;
  watch: UseFormWatch<OnboardingSportBackgroundFormInputs>;
  handleSubmit: UseFormHandleSubmit<OnboardingSportBackgroundFormInputs>;
}

interface OnboardingSportBackgroundFormProps {
  savedSportBackgrounds?: ISportBackground[];
  onFormTouchChange?: (isTouched: boolean) => void;
}

const DEFAULT_TEAM: ITeam = {
  isIndividualSport: false,
  isActiveTeam: false,
  leagueId: '',
  leagueName: '',
  teamId: '',
  teamName: '',
  // Start with no dates selected so placeholders are shown until user picks
  startDateInTeam: undefined as any,
  endDateInTeam: undefined as any,
};

const DEFAULT_SPORT_BACKGROUND: ISportBackground = {
  sportId: '',
  isOtherSport: false,
  isIndividualSport: false,
  isActiveSport: false,
  startCareerDate: undefined as any,
  endCareerDate: undefined as any,
  description: '',
  teams: [DEFAULT_TEAM],
  fileData: [],
};

const OnboardingSportBackgroundForm = forwardRef<
  FormRef,
  OnboardingSportBackgroundFormProps
>(
  (
    { savedSportBackgrounds, onFormTouchChange },
    ref: ForwardedRef<FormRef>
  ) => {
    const theme = useTheme();
    const mobileView = useMediaQuery(theme.breakpoints.down('md'));

    const {
      register,
      formState: { errors, isDirty, touchedFields },
      control,
      handleSubmit,
      reset,
      watch,
      setValue,
      trigger,
    } = useForm<OnboardingSportBackgroundFormInputs>({
      resolver: yupResolver(onboardingSportBackgroundFormSchema, {
        abortEarly: false,
      }),
      defaultValues: {
        sportBackgrounds:
          savedSportBackgrounds && savedSportBackgrounds.length > 0
            ? savedSportBackgrounds
            : [DEFAULT_SPORT_BACKGROUND],
      },
      mode: 'onSubmit',
      reValidateMode: 'onChange',
    });

    const { data: sports = [], isLoading: _isSportsLoading } =
      useGetSportsQuery();

    const {
      fields: sportFields,
      append: appendSport,
      remove: removeSport,
    } = useFieldArray({ control, name: 'sportBackgrounds' });

    useImperativeHandle(ref, () => ({ control, reset, watch, handleSubmit }));

    const watchedValues = watch();

    useEffect(() => {
      if (!onFormTouchChange) return;

      // Treat callback as "form can proceed" flag.
      // Allow proceeding either when the user has interacted AND the form is valid,
      // or when we are hydrating from previously saved sport backgrounds that
      // have already passed validation in a prior step.
      const hasTouched = Object.keys(touchedFields).length > 0;
      const hasSaved =
        !!savedSportBackgrounds && savedSportBackgrounds.length > 0;
      const canProceed = hasTouched || isDirty || hasSaved;
      onFormTouchChange(canProceed);
    }, [
      touchedFields,
      watchedValues,
      isDirty,
      savedSportBackgrounds,
      onFormTouchChange,
    ]);

    return (
      <>
        {sportFields.map((item, sportIndex) => {
          const isActiveSport = watch(
            `sportBackgrounds.${sportIndex}.isActiveSport` as const
          );
          const selectedSportId = watch(
            `sportBackgrounds.${sportIndex}.sportId` as const
          );
          const isOtherSport = watch(
            `sportBackgrounds.${sportIndex}.isOtherSport` as const
          );
          const isIndividualSport = watch(
            `sportBackgrounds.${sportIndex}.isIndividualSport` as const
          );

          const handleSportToggle = (sportId: string) => {
            const sport = sports.find((s: any) => s.id === sportId);
            const isOther = sportId === OTHER_SPORT.id;

            setValue(
              `sportBackgrounds.${sportIndex}.sportId` as const,
              sportId
            );
            setValue(
              `sportBackgrounds.${sportIndex}.sportName` as const,
              sport?.name || ''
            );
            setValue(
              `sportBackgrounds.${sportIndex}.isOtherSport` as const,
              isOther
            );

            if (!isOther) {
              // Reset custom name and individual flag when choosing a predefined sport
              setValue(
                `sportBackgrounds.${sportIndex}.isIndividualSport` as const,
                false
              );
            }
          };

          return (
            <Fragment key={item.id}>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
              >
                {sportFields.length > 1 && (
                  <IconButton onClick={() => removeSport(sportIndex)}>
                    <DeleteFileRedIcon />
                  </IconButton>
                )}
              </Box>

              <Grid
                container
                sx={{ rowGap: '28px', '& > .MuiGrid-item': { width: 1 } }}
              >
                {/* Sport selection */}
                <Grid item>
                  <Box display="flex" flexDirection="column" gap="16px">
                    <Typography variant="h3">What did you play?</Typography>
                    <SportList
                      sports={sports}
                      selectedSportId={selectedSportId || null}
                      handleSportToggle={handleSportToggle}
                    />
                    {errors.sportBackgrounds?.[sportIndex]?.sportId && (
                      <Typography variant="caption" color="error.main">
                        {(errors.sportBackgrounds?.[sportIndex] as any)?.sportId
                          ?.message || 'Sport is required'}
                      </Typography>
                    )}

                    {isOtherSport && (
                      <Box
                        mt="24px"
                        display="flex"
                        flexDirection="column"
                        gap="16px"
                      >
                        <Input
                          caption="Sport name"
                          variant="outlined"
                          fullWidth
                          placeholder="Enter sport name"
                          register={register(
                            `sportBackgrounds.${sportIndex}.sportName` as const
                          )}
                          error={
                            !!errors.sportBackgrounds?.[sportIndex]?.sportName
                          }
                          helperText={
                            (errors.sportBackgrounds?.[sportIndex] as any)
                              ?.sportName?.message || ''
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                !!watch(
                                  `sportBackgrounds.${sportIndex}.isIndividualSport` as const
                                )
                              }
                              onChange={(_, checked) =>
                                setValue(
                                  `sportBackgrounds.${sportIndex}.isIndividualSport` as const,
                                  checked
                                )
                              }
                              sx={{ padding: 0, margin: 0 }}
                            />
                          }
                          label={
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 400, fontSize: '14px' }}
                            >
                              Individual sport
                            </Typography>
                          }
                          sx={{
                            padding: 0,
                            margin: 0,
                            gap: '8px',
                            width: '100%',
                            alignItems: 'center',
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Ongoing checkbox + career dates */}
                <Grid item>
                  <Box display="flex" flexDirection="column">
                    <Typography
                      variant="body4"
                      fontWeight={700}
                      padding={'0 2px'}
                    >
                      Your career timeline:
                    </Typography>
                    <Grid
                      container
                      sx={(theme) => ({
                        display: 'flex',
                        gap: '1.375rem',
                        [theme.breakpoints.down('md')]: {
                          gridColumn: 'span 2',
                          display: 'block',
                          gap: '1rem',
                          width: '100%',
                        },
                      })}
                    >
                      <Grid item width={mobileView ? '100%' : 184}>
                        <DateInput<OnboardingSportBackgroundFormInputs>
                          control={control}
                          name={
                            `sportBackgrounds.${sportIndex}.startCareerDate` as const
                          }
                          error={
                            errors.sportBackgrounds?.[sportIndex]
                              ?.startCareerDate as any
                          }
                          helperText={
                            (errors.sportBackgrounds?.[sportIndex] as any)
                              ?.startCareerDate?.message || ''
                          }
                          placeholder="Your start date"
                          disabled={false}
                        />
                      </Grid>
                      <Grid item width={mobileView ? '100%' : 184}>
                        {!isActiveSport && (
                          <DateInput<OnboardingSportBackgroundFormInputs>
                            control={control}
                            name={
                              `sportBackgrounds.${sportIndex}.endCareerDate` as const
                            }
                            error={
                              errors.sportBackgrounds?.[sportIndex]
                                ?.endCareerDate as any
                            }
                            helperText={
                              (errors.sportBackgrounds?.[sportIndex] as any)
                                ?.endCareerDate?.message || ''
                            }
                            placeholder="Your end date"
                            disabled={false}
                          />
                        )}
                      </Grid>
                    </Grid>
                    <Box pt="36px">
                      <Controller
                        name={
                          `sportBackgrounds.${sportIndex}.isActiveSport` as const
                        }
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!field.value}
                                onChange={(_, checked) => {
                                  field.onChange(checked);
                                }}
                                sx={{
                                  padding: 0,
                                  margin: 0,
                                }}
                              />
                            }
                            label={
                              <Typography
                                variant={'caption'}
                                sx={{ fontWeight: 400, fontSize: '14px' }}
                              >
                                My career in athletics is ongoing
                              </Typography>
                            }
                            sx={{
                              padding: 0,
                              margin: 0,
                              gap: '8px',
                              width: '100%',
                              alignItems: 'center',
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Description */}
                <Grid item>
                  <Box
                    display="flex"
                    gridColumn="span 2"
                    rowGap={mobileView ? '1.75rem' : '36px'}
                    columnGap="16px"
                    mt="36px"
                  >
                    <Input
                      caption="Describe your sport background:"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Few words about your sports experience"
                      register={register(
                        `sportBackgrounds.${sportIndex}.description` as const
                      )}
                      error={
                        !!errors.sportBackgrounds?.[sportIndex]?.description
                      }
                      helperText={
                        (errors.sportBackgrounds?.[sportIndex] as any)
                          ?.description?.message || ''
                      }
                      containerFlex="1"
                    />
                  </Box>
                </Grid>

                {/* Teams section */}
                <Grid item>
                  <Typography variant="h3" sx={{ mb: '12px' }}>
                    {isIndividualSport ? 'Leagues' : 'Teams'}
                  </Typography>

                  <TeamsFieldArray
                    control={control}
                    register={register}
                    sportIndex={sportIndex}
                    errors={errors}
                    setValue={setValue}
                    trigger={trigger}
                    isIndividualSport={isIndividualSport}
                  />
                </Grid>

                {/* Attachments (last, optional) */}
                <Grid item>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={mobileView ? '1.75rem' : '2.25rem'}
                  >
                    <Box display="flex" flexDirection="column" gap="12px">
                      <Typography variant={'h3'}>
                        Confirm Your Sport Experience
                      </Typography>
                      <Typography variant={'body3'}>
                        Upload photos, videos or documents of your awards,
                        certificates, etc.
                      </Typography>
                    </Box>

                    {(
                      (watch(
                        `sportBackgrounds.${sportIndex}.fileData` as const
                      ) as ISportBackgroundFileData[] | undefined) || []
                    ).length > 0 && (
                      <Box display="flex" flexDirection={'column'} gap="22px">
                        {(
                          (watch(
                            `sportBackgrounds.${sportIndex}.fileData` as const
                          ) as ISportBackgroundFileData[] | undefined) || []
                        ).map((fileData, fileIndex) => {
                          const allFiles =
                            (watch(
                              `sportBackgrounds.${sportIndex}.fileData` as const
                            ) as ISportBackgroundFileData[] | undefined) || [];

                          const removeFileFromBackground = (file: File) => {
                            const next = allFiles.filter(
                              (item) => item.file !== file
                            );
                            setValue(
                              `sportBackgrounds.${sportIndex}.fileData` as const,
                              next
                            );
                          };

                          const handleFileDataChange = (
                            index: number,
                            data: Partial<ISportBackgroundFileData>
                          ) => {
                            const next = [...allFiles];
                            next[index] = { ...next[index], ...data };
                            setValue(
                              `sportBackgrounds.${sportIndex}.fileData` as const,
                              next
                            );
                          };

                          return (
                            <Box
                              key={fileIndex}
                              width="100%"
                              padding={'22px'}
                              border={'1px solid'}
                              borderColor={'background.darker'}
                              borderRadius={'1px'}
                            >
                              <SportBackgroundImageFileItem
                                fileData={fileData}
                                index={fileIndex}
                                removeFileFromBackground={() =>
                                  removeFileFromBackground(fileData.file)
                                }
                                handleFileDataChange={handleFileDataChange}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    <DragAndDropBox
                      handleDragOver={(event) => {
                        event.preventDefault();
                      }}
                      handleDragLeave={() => {}}
                      handleDrop={(event) => {
                        event.preventDefault();
                        const sportId = watch(
                          `sportBackgrounds.${sportIndex}.sportId` as const
                        );
                        if (!sportId) {
                          return;
                        }

                        const files = Array.from(
                          event.dataTransfer.files
                        ).filter((file) =>
                          isSupportedSportBackgroundFileHelper(file)
                        );

                        if (!files.length) return;

                        // Validate file sizes
                        const validFiles: File[] = [];
                        const invalidFiles: string[] = [];

                        files.forEach((file) => {
                          const sizeValidation = validateFileSize(file);
                          if (sizeValidation.valid) {
                            validFiles.push(file);
                          } else if (sizeValidation.error) {
                            invalidFiles.push(sizeValidation.error);
                          }
                        });

                        if (invalidFiles.length > 0) {
                          alert(invalidFiles.join('\n'));
                        }

                        if (!validFiles.length) return;

                        const existing =
                          (watch(
                            `sportBackgrounds.${sportIndex}.fileData` as const
                          ) as ISportBackgroundFileData[] | undefined) || [];

                        const next: ISportBackgroundFileData[] = [
                          ...existing,
                          ...validFiles.map((file) => ({ file })),
                        ];

                        setValue(
                          `sportBackgrounds.${sportIndex}.fileData` as const,
                          next
                        );
                      }}
                      handleFileChange={(event) => {
                        const sportId = watch(
                          `sportBackgrounds.${sportIndex}.sportId` as const
                        );
                        if (!sportId) {
                          event.target.value = '';
                          return;
                        }

                        const files = Array.from(
                          event.target.files || []
                        ).filter((file) =>
                          isSupportedSportBackgroundFileHelper(file)
                        );
                        if (!files.length) {
                          event.target.value = '';
                          return;
                        }

                        // Validate file sizes
                        const validFiles: File[] = [];
                        const invalidFiles: string[] = [];

                        files.forEach((file) => {
                          const sizeValidation = validateFileSize(file);
                          if (sizeValidation.valid) {
                            validFiles.push(file);
                          } else if (sizeValidation.error) {
                            invalidFiles.push(sizeValidation.error);
                          }
                        });

                        if (invalidFiles.length > 0) {
                          alert(invalidFiles.join('\n'));
                        }

                        if (!validFiles.length) {
                          event.target.value = '';
                          return;
                        }

                        const existing =
                          (watch(
                            `sportBackgrounds.${sportIndex}.fileData` as const
                          ) as ISportBackgroundFileData[] | undefined) || [];

                        const next: ISportBackgroundFileData[] = [
                          ...existing,
                          ...validFiles.map((file) => ({ file })),
                        ];

                        setValue(
                          `sportBackgrounds.${sportIndex}.fileData` as const,
                          next
                        );

                        event.target.value = '';
                      }}
                      triggerFileInput={() => {
                        const input = document.getElementById(
                          `${ElementIdEnum.SPORT_BACKGROUND_FILE_INPUT}-${sportIndex}`
                        ) as HTMLInputElement | null;

                        if (input) {
                          input.click();
                        }
                      }}
                      fileInputId={`${ElementIdEnum.SPORT_BACKGROUND_FILE_INPUT}-${sportIndex}`}
                      icon={<UploadIcon />}
                      text={
                        <>
                          Images (JPEG, PNG, WebP), PDFs, or Videos (MP4, AVI)
                          are accepted.
                        </>
                      }
                    />
                  </Box>
                </Grid>
              </Grid>

              {sportFields.length > 1 &&
                sportIndex !== sportFields.length - 1 && (
                  <Divider sx={{ my: '36px' }} />
                )}
            </Fragment>
          );
        })}

        <CustomButton
          buttonWidth="100%"
          variantType={ButtonTypeEnum.SECONDARY}
          sx={{ borderColor: 'background.dark', mt: '36px', height: 58 }}
          onClick={() =>
            appendSport(DEFAULT_SPORT_BACKGROUND as unknown as ISportBackground)
          }
          type="button"
        >
          ADD SPORT BACKGROUND
        </CustomButton>
      </>
    );
  }
);

OnboardingSportBackgroundForm.displayName = 'OnboardingSportBackgroundForm';

interface TeamsFieldArrayProps {
  control: Control<OnboardingSportBackgroundFormInputs, any>;
  register: any;
  sportIndex: number;
  errors: any;
  setValue: UseFormSetValue<OnboardingSportBackgroundFormInputs>;
  trigger: any;
  isIndividualSport?: boolean;
}

const TeamsFieldArray = ({
  control,
  register: _register,
  sportIndex,
  errors,
  setValue,
  trigger,
  isIndividualSport = false,
}: TeamsFieldArrayProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `sportBackgrounds.${sportIndex}.teams` as const,
  });

  return (
    <>
      {fields.map((field, teamIndex) => (
        <TeamRow
          key={field.id}
          control={control}
          sportIndex={sportIndex}
          teamIndex={teamIndex}
          errors={errors}
          onRemove={() => remove(teamIndex)}
          canRemove={fields.length > 1}
          setValue={setValue}
          trigger={trigger}
          isIndividualSport={isIndividualSport}
        />
      ))}

      <CustomButton
        buttonWidth="100%"
        variantType={ButtonTypeEnum.SECONDARY}
        sx={{ borderColor: 'background.dark', mt: '16px', height: 50 }}
        onClick={() =>
          append({
            ...DEFAULT_TEAM,
            isIndividualSport: !!isIndividualSport,
          } as unknown as ITeam)
        }
        type="button"
      >
        {isIndividualSport ? 'ADD LEAGUE' : 'ADD TEAM'}
      </CustomButton>
    </>
  );
};

interface TeamRowProps {
  control: Control<OnboardingSportBackgroundFormInputs, any>;
  sportIndex: number;
  teamIndex: number;
  errors: any;
  onRemove: () => void;
  canRemove: boolean;
  setValue: UseFormSetValue<OnboardingSportBackgroundFormInputs>;
  trigger: any;
  isIndividualSport?: boolean;
}

const TeamRow = ({
  control,
  sportIndex,
  teamIndex,
  errors,
  onRemove,
  canRemove,
  setValue,
  trigger,
  isIndividualSport = false,
}: TeamRowProps) => {
  const [typedLeague, setTypedLeague] = useState('');
  const [typedTeam, setTypedTeam] = useState('');
  const hasHydratedText = useRef(false);

  const sportName = useWatch({
    control,
    name: `sportBackgrounds.${sportIndex}.sportName` as const,
  });

  const leagueId = useWatch({
    control,
    name: `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueId` as const,
  });

  const isActiveTeam = useWatch({
    control,
    name: `sportBackgrounds.${sportIndex}.teams.${teamIndex}.isActiveTeam` as const,
  });

  const leagueNameValue = useWatch({
    control,
    name: `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueName` as const,
  });

  const teamNameValue = useWatch({
    control,
    name: `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const,
  });

  useEffect(() => {
    // Hydrate typed inputs from saved values once on mount/first load
    if (hasHydratedText.current) return;

    if (leagueNameValue) {
      setTypedLeague(leagueNameValue as string);
    }

    if (teamNameValue) {
      setTypedTeam(teamNameValue as string);
    }

    if (leagueNameValue || teamNameValue) {
      hasHydratedText.current = true;
    }
  }, [leagueNameValue, teamNameValue]);

  useEffect(() => {
    setValue(
      `sportBackgrounds.${sportIndex}.teams.${teamIndex}.isIndividualSport` as const,
      !!isIndividualSport
    );
  }, [isIndividualSport, setValue, sportIndex, teamIndex]);

  const { data: leagues = [], isLoading: isLeagueLoading } =
    useGetLeagueBySportNameQuery(withUnderScore(sportName || ''), {
      skip: !sportName,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
    });

  const { data: teams = [], isLoading: isTeamLoading } =
    useGetTeamsByLeagueIdOrExternalIdQuery(
      withUnderScore(leagueId || 'other'),
      {
        skip: !leagueId,
      }
    );

  const leagueError =
    errors.sportBackgrounds?.[sportIndex]?.teams?.[teamIndex]?.leagueName;
  const teamError =
    errors.sportBackgrounds?.[sportIndex]?.teams?.[teamIndex]?.teamName;

  return (
    <Box mb="24px">
      <Box display="flex" justifyContent="flex-end" mb="8px">
        {canRemove && (
          <IconButton onClick={onRemove}>
            <DeleteFileRedIcon />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Controller
            control={control}
            name={
              `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueId` as const
            }
            render={({ field }) => {
              const selectedLeague =
                leagues.find((l: any) => l.idLeague === field.value) || null;

              return (
                <FormControl
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  variant="outlined"
                  fullWidth
                  error={!!leagueError}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      padding: '0 2px',
                      mb: '12px',
                      color: 'text.primary',
                    }}
                  >
                    League:
                  </Typography>
                  <Autocomplete
                    freeSolo
                    options={leagues ?? []}
                    loading={isLeagueLoading}
                    getOptionLabel={(option: any) =>
                      typeof option === 'string'
                        ? option
                        : option.strLeague || ''
                    }
                    isOptionEqualToValue={(option: any, value: any) =>
                      typeof value === 'object' &&
                      option.idLeague === value?.idLeague
                    }
                    value={selectedLeague || typedLeague}
                    inputValue={typedLeague}
                    onInputChange={(_, newInputValue) => {
                      setTypedLeague(newInputValue);
                      setValue(
                        `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueName` as const,
                        newInputValue
                      );
                      // Trigger validation to clear error if input is now valid
                      trigger(
                        `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueName`
                      );
                      // Reset team when league text changes
                      setValue(
                        `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamId` as const,
                        ''
                      );
                      setValue(
                        `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const,
                        ''
                      );
                      setTypedTeam('');
                      field.onChange('');
                    }}
                    onChange={(_, newValue: any) => {
                      if (newValue && typeof newValue === 'object') {
                        field.onChange(newValue.idLeague);
                        setTypedLeague(newValue.strLeague);
                        setValue(
                          `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueName` as const,
                          newValue.strLeague
                        );
                      } else if (typeof newValue === 'string') {
                        field.onChange('');
                        setTypedLeague(newValue);
                        setValue(
                          `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueName` as const,
                          newValue
                        );
                      } else {
                        field.onChange('');
                        setTypedLeague('');
                        setValue(
                          `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueName` as const,
                          ''
                        );
                        setValue(
                          `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamId` as const,
                          ''
                        );
                        setValue(
                          `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const,
                          ''
                        );
                        setTypedTeam('');
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name={`sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueName`}
                        placeholder="Search or enter a league"
                        error={!!leagueError}
                        helperText={leagueError?.message || ''}
                      />
                    )}
                    renderOption={(props, option: any) => (
                      <AutocompleteOption
                        text={option.strLeague}
                        props={props}
                      />
                    )}
                    popupIcon={<ArrowDownIcon />}
                    sx={{
                      '.MuiInputBase-root': {
                        height: '50px',
                      },
                      '.MuiSvgIcon-root': {
                        bgcolor: 'transparent !important',
                      },
                    }}
                  />
                </FormControl>
              );
            }}
          />
        </Grid>
        {/* Team Field - Hide for individual sports */}
        {!isIndividualSport && (
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name={
                `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamId` as const
              }
              render={({ field }) => {
                const selectedTeam =
                  teams.find((t: any) => t.idTeam === field.value) || null;

                return (
                  <FormControl
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    variant="outlined"
                    fullWidth
                    error={!!teamError}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        padding: '0 2px',
                        mb: '12px',
                        color: 'text.primary',
                      }}
                    >
                      Team:
                    </Typography>
                    <Autocomplete
                      freeSolo
                      options={teams ?? []}
                      loading={isTeamLoading}
                      getOptionLabel={(option: any) =>
                        typeof option === 'string'
                          ? option
                          : option.strTeam || ''
                      }
                      isOptionEqualToValue={(option: any, value: any) =>
                        typeof value === 'object' &&
                        option.idTeam === value?.idTeam
                      }
                      value={selectedTeam || typedTeam}
                      inputValue={typedTeam}
                      onInputChange={(_, newInputValue) => {
                        setTypedTeam(newInputValue);
                        setValue(
                          `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const,
                          newInputValue
                        );
                        // Trigger validation to clear error if input is now valid
                        trigger(
                          `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName`
                        );
                        field.onChange('');
                      }}
                      onChange={(_, newValue: any) => {
                        if (newValue && typeof newValue === 'object') {
                          field.onChange(newValue.idTeam);
                          setTypedTeam(newValue.strTeam);
                          setValue(
                            `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const,
                            newValue.strTeam
                          );
                        } else if (typeof newValue === 'string') {
                          field.onChange('');
                          setTypedTeam(newValue);
                          setValue(
                            `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const,
                            newValue
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name={`sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName`}
                          placeholder="Search or enter a team"
                          error={!!teamError}
                          helperText={teamError?.message || ''}
                          disabled={!leagueId && !typedLeague}
                        />
                      )}
                      renderOption={(props, option: any) => (
                        <AutocompleteOption
                          text={option.strTeam}
                          props={props}
                        />
                      )}
                      popupIcon={<ArrowDownIcon />}
                      disabled={!leagueId && !typedLeague}
                      sx={{
                        '.MuiInputBase-root': {
                          height: '50px',
                        },
                        '.MuiSvgIcon-root': {
                          bgcolor: 'transparent !important',
                        },
                      }}
                    />
                  </FormControl>
                );
              }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="caption" sx={{ padding: '0 2px', mb: '12px' }}>
            Timeline:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} sx={{ width: '100%' }}>
              <DateInput<OnboardingSportBackgroundFormInputs>
                control={control}
                name={
                  `sportBackgrounds.${sportIndex}.teams.${teamIndex}.startDateInTeam` as const
                }
                error={
                  errors.sportBackgrounds?.[sportIndex]?.teams?.[teamIndex]
                    ?.startDateInTeam as any
                }
                helperText={
                  (
                    errors.sportBackgrounds?.[sportIndex]?.teams?.[
                      teamIndex
                    ] as any
                  )?.startDateInTeam?.message || ''
                }
                placeholder="Your start date"
                disabled={false}
              />
            </Grid>
            {!isActiveTeam && (
              <Grid item xs={12} md={6}>
                <DateInput<OnboardingSportBackgroundFormInputs>
                  control={control}
                  name={
                    `sportBackgrounds.${sportIndex}.teams.${teamIndex}.endDateInTeam` as const
                  }
                  error={
                    errors.sportBackgrounds?.[sportIndex]?.teams?.[teamIndex]
                      ?.endDateInTeam as any
                  }
                  helperText={
                    (
                      errors.sportBackgrounds?.[sportIndex]?.teams?.[
                        teamIndex
                      ] as any
                    )?.endDateInTeam?.message || ''
                  }
                  placeholder="Your end date"
                  disabled={false}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name={
              `sportBackgrounds.${sportIndex}.teams.${teamIndex}.isActiveTeam` as const
            }
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={(_, checked) => {
                      field.onChange(checked);
                    }}
                    sx={{ padding: 0, margin: 0 }}
                  />
                }
                label={
                  <Typography
                    variant={'caption'}
                    sx={{ fontWeight: 400, fontSize: '14px' }}
                  >
                    I am currently on this team
                  </Typography>
                }
                sx={{
                  padding: 0,
                  margin: 0,
                  gap: '8px',
                  width: '100%',
                  alignItems: 'center',
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OnboardingSportBackgroundForm;
